// rotationScore.js — pure client-side scoring for Rotation suggestions.

import { normSeason } from './displayLabels.js'
import { localDayISO } from './localDay.js'

const SOUTHERN_REGION_TERMS = [
  'australia', 'new zealand', 'south africa', 'argentina', 'chile',
  'uruguay', 'paraguay', 'southern hemisphere',
]

const SEASON_ORDER = ['Spring', 'Summer', 'Fall', 'Winter']

export const ROTATION_OCCASIONS = Object.freeze([
  { value: '', label: 'Surprise me' },
  { value: 'date', label: 'Date Night' },
  { value: 'evening', label: 'Evening Out' },
  { value: 'formal', label: 'Formal Event' },
  { value: 'office', label: 'Office' },
  { value: 'casual', label: 'Casual' },
  { value: 'vacation', label: 'Vacation' },
])

export const ROTATION_WEATHER = Object.freeze([
  { value: '', label: 'Auto (season-based)' },
  { value: 'hot', label: 'Hot (>85°F)' },
  { value: 'warm', label: 'Warm (70-85°F)' },
  { value: 'mild', label: 'Mild (55-70°F)' },
  { value: 'cool', label: 'Cool (40-55°F)' },
  { value: 'cold', label: 'Cold (<40°F)' },
])

export const ROTATION_LIVE_WEATHER = Object.freeze([
  { ...ROTATION_WEATHER[0], label: 'Auto (live weather)' },
  ...ROTATION_WEATHER.slice(1),
])

export const ROTATION_MOODS = Object.freeze([
  { value: '', label: 'No preference' },
  { value: 'bold', label: 'Bold & loud' },
  { value: 'smooth', label: 'Smooth & confident' },
  { value: 'fresh', label: 'Fresh & clean' },
  { value: 'cozy', label: 'Cozy & warm' },
  { value: 'mysterious', label: 'Dark & mysterious' },
])

export const ROTATION_NEGLECT = Object.freeze([
  { value: 'yes', label: 'Yes, rotate in forgotten ones' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'no', label: 'No, give me the hits' },
])

export const ROTATION_LAYERS = Object.freeze([
  { value: 'yes', label: 'Yes, suggest combos' },
  { value: 'no', label: 'No, just one fragrance' },
])

function isSouthernRegion(region = '') {
  const normalized = String(region).toLocaleLowerCase()
  return SOUTHERN_REGION_TERMS.some(term => normalized.includes(term))
}

// Calendar month → local season. Region is the existing user-profile free-text
// preference; known southern-hemisphere regions invert the calendar season.
export function currentSeason(date = new Date(), region = '') {
  const month = date.getMonth()
  let index
  if (month >= 2 && month <= 4) index = 0
  else if (month >= 5 && month <= 7) index = 1
  else if (month >= 8 && month <= 10) index = 2
  else index = 3
  if (isSouthernRegion(region)) index = (index + 2) % 4
  return SEASON_ORDER[index]
}

export function temperatureToWeather(temperatureF) {
  if (!Number.isFinite(temperatureF)) return ''
  if (temperatureF > 85) return 'hot'
  if (temperatureF >= 70) return 'warm'
  if (temperatureF >= 55) return 'mild'
  if (temperatureF >= 40) return 'cool'
  return 'cold'
}

export function weatherToSeasons(weather, { date = new Date(), region = '', temperatureF } = {}) {
  const effectiveWeather = weather || temperatureToWeather(temperatureF)
  switch (effectiveWeather) {
    case 'hot': return ['Summer']
    case 'warm': return ['Spring', 'Summer']
    case 'mild': return ['Spring', 'Fall']
    case 'cool': return ['Fall', 'Winter']
    case 'cold': return ['Winter']
    default: return [currentSeason(date, region)]
  }
}

export function freshRotationWeatherSnapshot(logs = [], now = new Date()) {
  const instant = now instanceof Date ? now : new Date(now)
  if (Number.isNaN(instant.getTime())) return null
  const today = localDayISO(instant)
  return (logs || [])
    .filter(log => log?.worn_on === today)
    .map(log => log?.context_weather)
    .filter(snapshot => {
      const fetchedAt = new Date(snapshot?.fetched_at)
      return Number.isFinite(snapshot?.temp_c)
        && snapshot.temp_c >= -60
        && snapshot.temp_c <= 60
        && typeof snapshot?.condition === 'string'
        && snapshot.condition.trim().length > 0
        && Number.isFinite(snapshot?.humidity_pct)
        && snapshot.humidity_pct >= 0
        && snapshot.humidity_pct <= 100
        && !Number.isNaN(fetchedAt.getTime())
        && fetchedAt <= instant
        && localDayISO(fetchedAt) === today
    })
    .sort((left, right) => Date.parse(right.fetched_at) - Date.parse(left.fetched_at))[0] || null
}

export function rotationWeatherReading(snapshot) {
  if (!snapshot) return null
  const temperatureF = Math.round((snapshot.temp_c * 9) / 5 + 32)
  return {
    temperatureF,
    text: `Using ${temperatureF}°F, ${snapshot.condition.trim().toLocaleLowerCase()} · ${Math.round(snapshot.humidity_pct)}% humidity`,
  }
}

function arrayValue(value) {
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value === 'string') return value.split(',').map(item => item.trim()).filter(Boolean)
  return []
}

function normalizedText(value) {
  return arrayValue(value).join(' ').toLocaleLowerCase()
}

function seasonMatches(fragrance, seasons) {
  const normalizedSeasons = seasons.map(normSeason)
  const catalogSeasons = arrayValue(fragrance?.seasons).map(normSeason)
  return normalizedSeasons.some(season => (
    catalogSeasons.includes(season)
    || fragrance?.[season.toLocaleLowerCase()] === true
  ))
}

function peakSeasonMatches(fragrance, seasons) {
  const peak = normalizedText(fragrance?.peak_season ?? fragrance?.peakSeason)
  return seasons.some(season => peak.includes(String(season).toLocaleLowerCase()))
}

export function occasionFit(fragrance, occasion) {
  if (!occasion) return 0
  const occasions = normalizedText(fragrance?.occasions ?? fragrance?.occasion)
  switch (occasion) {
    case 'date': return occasions.includes('date') ? 3 : occasions.includes('evening') || occasions.includes('night out') ? 2 : 0
    case 'evening': return occasions.includes('evening') || occasions.includes('night out') ? 3 : occasions.includes('date') ? 2 : 0
    case 'formal': return occasions.includes('formal') || occasions.includes('event') ? 3 : occasions.includes('office') || occasions.includes('work') ? 1 : 0
    case 'office': return occasions.includes('office') || occasions.includes('work') ? 3 : occasions.includes('formal') ? 1 : 0
    case 'casual': return occasions.includes('casual') ? 3 : occasions.includes('vacation') ? 2 : 0
    case 'vacation': return occasions.includes('vacation') || occasions.includes('casual') ? 3 : 0
    default: return 0
  }
}

function fragranceMoodText(fragrance) {
  return [
    fragrance?.accord_tags,
    fragrance?.top_notes,
    fragrance?.mid_notes,
    fragrance?.base_notes,
    fragrance?.all_notes,
  ].map(normalizedText).filter(Boolean).join(' ')
}

export function moodFit(fragrance, mood) {
  if (!mood) return 0
  const profile = fragranceMoodText(fragrance)
  const projection = String(fragrance?.projection || '').toLocaleLowerCase()
  switch (mood) {
    case 'bold': return projection.includes('loud') ? 3 : profile.includes('amber') ? 1 : 0
    case 'smooth': return profile.includes('wood') ? 2 : profile.includes('amber') ? 1 : 0
    case 'fresh': return ['citrus', 'marine', 'aquatic'].some(term => profile.includes(term)) ? 3 : 0
    case 'cozy': return profile.includes('gourmand') ? 3 : profile.includes('amber') ? 1 : 0
    case 'mysterious': return ['wood', 'resin'].some(term => profile.includes(term)) ? 2 : 0
    default: return 0
  }
}

export function isGymWearLog(log) {
  const context = `${log?.wear_label || ''} ${log?.occasion || ''} ${log?.context || ''} ${log?.evening_context || ''}`
    .toLocaleLowerCase()
  return context.includes('gym')
}

function fragranceIds(fragrance) {
  return new Set([
    fragrance?.id,
    ...(fragrance?.sizes || []).map(size => size?.id),
  ].filter(Boolean).map(String))
}

function logMatchesFragrance(log, fragrance) {
  const ids = fragranceIds(fragrance)
  if (log?.fragrance_id && ids.has(String(log.fragrance_id))) return true
  return String(log?.fragrance_name || '').trim().toLocaleLowerCase() === String(fragrance?.name || '').trim().toLocaleLowerCase()
    && String(log?.house || '').trim().toLocaleLowerCase() === String(fragrance?.house || '').trim().toLocaleLowerCase()
}

export function wearCountForFragrance(fragrance, wearLogs = []) {
  return wearLogs.filter(log => !isGymWearLog(log) && logMatchesFragrance(log, fragrance)).length
}

function preferenceBoost(fragrance) {
  const weight = Number(fragrance?.preference_weight ?? fragrance?.preferenceWeight ?? 0)
  const explicitlyPreferred = fragrance?.signature === true
    || fragrance?.is_signature === true
    || fragrance?.favorite === true
    || fragrance?.favourite === true
    || fragrance?.is_favorite === true
    || fragrance?.is_favourite === true
  // preference_weight is the legacy persisted signature/favourite signal.
  // Explicit boolean variants remain supported for imported fixture rows.
  return explicitlyPreferred || Number.isFinite(weight) && weight !== 0
    ? Math.max(0, weight) * 2
    : 0
}

function genderRank(fragrance, preference) {
  if (!preference) return 0
  const gender = String(fragrance?.gender_class || fragrance?.gender || '').toLocaleLowerCase()
  return gender.includes(String(preference).toLocaleLowerCase()) ? 1 : 0
}

function isActiveCandidate(fragrance) {
  return !fragrance?.given_away && !fragrance?.finished && !fragrance?.finished_at
}

export function scoreFragrance(fragrance, {
  seasons = [currentSeason()],
  occasion = '',
  mood = '',
  neglect = 'balanced',
  wearLogs = [],
  genderPreference = '',
  random = Math.random,
} = {}) {
  let score = 50
  const reasons = []

  if (seasonMatches(fragrance, seasons)) {
    score += 15
    reasons.push('Season match')
  } else {
    score -= 20
  }

  if (peakSeasonMatches(fragrance, seasons)) {
    score += 4
    reasons.push('Peak season')
  }

  const occasionScore = occasionFit(fragrance, occasion)
  score += occasionScore * 5
  if (occasionScore >= 2) reasons.push('Occasion fit')

  const moodScore = moodFit(fragrance, mood)
  score += moodScore * 4
  if (moodScore >= 2) reasons.push('Mood match')

  const wears = wearCountForFragrance(fragrance, wearLogs)
  if (neglect === 'yes') {
    if (wears === 0) {
      score += 20
      reasons.push('Never worn')
    } else if (wears <= 2) {
      score += 10
      reasons.push('Rarely worn')
    }
  } else if (neglect === 'no') {
    score += wears * 3
    if (wears > 3) reasons.push('Proven favorite')
  }

  score += preferenceBoost(fragrance)
  score += Math.max(0, Math.min(1, Number(random()) || 0)) * 18

  const role = String(fragrance?.layering_role ?? fragrance?.layeringRole ?? '').toLocaleLowerCase()
  if (role === 'star') score += 2
  if (role === 'amplifier') score += 1

  return {
    ...fragrance,
    _score: Math.round(score),
    _reasons: reasons,
    _wearCount: wears,
    _genderRank: genderRank(fragrance, genderPreference),
  }
}

export function scoreFragrances(fragrances, options = {}) {
  return (fragrances || [])
    .filter(isActiveCandidate)
    .map(fragrance => scoreFragrance(fragrance, options))
    // Gender is a secondary rank only. It never filters or subtracts score.
    .sort((left, right) => right._score - left._score || right._genderRank - left._genderRank)
}

export function neverWornFragrances(fragrances, {
  seasons = [currentSeason()],
  wearLogs = [],
  limit = 12,
} = {}) {
  return (fragrances || [])
    .filter(isActiveCandidate)
    .filter(fragrance => wearCountForFragrance(fragrance, wearLogs) === 0)
    .filter(fragrance => seasonMatches(fragrance, seasons))
    .slice(0, limit)
}

export function rotationReason(fragrance) {
  return fragrance?._reasons?.slice(0, 2).join(', ') || 'A fresh pick from your shelf'
}
